import { Comment, CommentDraft, CommentInput } from "@asksyntheia/comments";
import { memo, useCallback } from "react";
import ForumOutlineIcon from "~icons/material-symbols/forum-outline";
import { EmptyPlaceholder } from "../../components/EmptyPlaceholder";
import { LoadingContainer } from "../../components/Loading";
import { WorkspaceCommentsProvider } from "../../components/WorkspaceCommentsProvider";
import { commentApi } from "../../services";
import { RelatedObjectType } from "../../types/enums";

type CommentAppProps = {
  clauseId: string;
  workspaceId: string;
};

export const CommentApp = memo(({ clauseId, workspaceId }: CommentAppProps) => {
  const { data, isLoading } = commentApi.useGetCommentsQuery({
    workspaceId,
    filter: { relatedId: clauseId, clauseId },
  });

  const [createComment] = commentApi.useCreateCommentMutation();
  const [updateComment] = commentApi.useUpdateCommentMutation();
  const [deleteComment] = commentApi.useDeleteCommentMutation();
  const [toggleReaction] = commentApi.useToggleReactionMutation();

  const comments = (data ?? []).slice().sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const onSubmit = useCallback(
    async ({ text }: CommentDraft) => {
      try {
        await createComment({
          workspaceId,
          comment: { clauseId, text, relatedId: clauseId, relatedObjectType: RelatedObjectType.CLAUSE },
        }).unwrap();
      } catch (err) {
        console.error(err);
      }
    },
    [createComment, workspaceId, clauseId]
  );

  const onUpdateComment = useCallback(
    (commentId: string, newText: string) =>
      updateComment({ workspaceId, comment: { commentId, text: newText } }).unwrap(),
    [updateComment, workspaceId]
  );

  const onDeleteComment = useCallback(
    (commentId: string) => {
      deleteComment({ workspaceId, commentId });
    },
    [deleteComment, workspaceId]
  );

  const onReact = useCallback(
    (commentId: string, emoji: string) => {
      toggleReaction({ workspaceId, commentId, emoji });
    },
    [toggleReaction, workspaceId]
  );

  const onReply = useCallback(
    (parentCommentId: string, text: string) =>
      createComment({
        workspaceId,
        comment: {
          clauseId,
          text,
          relatedId: clauseId,
          relatedObjectType: RelatedObjectType.CLAUSE,
          parentCommentId,
        },
      }).unwrap(),
    [createComment, workspaceId, clauseId]
  );

  return (
    <WorkspaceCommentsProvider>
      <div className="flex w-full grow flex-col overflow-hidden">
        <LoadingContainer isLoading={isLoading} size="large" label="Loading..." labelPosition="below">
          <div className="flex grow flex-col gap-2 overflow-y-auto overflow-x-hidden px-2.5">
            {comments.length === 0 && (
              <EmptyPlaceholder text="No Comments" icon={<ForumOutlineIcon className="text-[32px]" />} />
            )}
            {comments.map((comment) => (
              <Comment
                key={comment.commentId}
                comment={comment}
                onDelete={onDeleteComment}
                onUpdate={onUpdateComment}
                onReact={onReact}
                onReply={onReply}
              />
            ))}
          </div>
        </LoadingContainer>
        <div className="shrink-0 p-2.5">
          <CommentInput onSubmit={onSubmit} placeholder="Write a comment..." />
        </div>
      </div>
    </WorkspaceCommentsProvider>
  );
});
